import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { DataTable } from '../../../../shared/components/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';

interface Tiles {
  readonly pendingReview: number;
  readonly inReview: number;
  readonly approved: number;
  readonly rejected: number;
  readonly clarificationRequired: number;
  readonly escalated: number;
  readonly onHold: number;
  readonly financeEligible: number;
  readonly overdue: number;
  readonly openExceptions: number;
  readonly pendingEscalations: number;
  readonly pendingClarifications: number;
}

interface QueueItem {
  readonly reviewId: string;
  readonly jobNumber: string | null;
  readonly status: string;
  readonly reviewerRoleCode: string;
  readonly priorityCode: string;
  readonly slaDeadlineAt: string;
  readonly estimatedCostInr: number | null;
  readonly isHighValue: boolean;
  readonly cycleNo: number;
}

interface BudgetSummary {
  readonly totalAllocated: number;
  readonly totalConsumed: number;
  readonly totalReserved: number;
  readonly utilizationPct: number;
  readonly activePlans: number;
}

interface RegionSummaryItem {
  readonly regionCode: string;
  readonly total: number;
  readonly approved: number;
  readonly pending: number;
}

interface TerritorySummaryItem {
  readonly territoryCode: string;
  readonly total: number;
  readonly approved: number;
  readonly pending: number;
}

const TILE_STYLE: React.CSSProperties = {
  padding: 16, borderRadius: 8,
  border: '1px solid var(--color-border-default)',
  background: 'var(--color-bg-elevated)',
  textAlign: 'center', minWidth: 120,
};

const NUM_STYLE: React.CSSProperties = {
  fontSize: 'var(--font-size-2xl, 28px)', fontWeight: 700,
  color: 'var(--color-brand-primary)',
};


function slaColor(iso: string): string {
  const d = new Date(iso);
  if (d < new Date()) return 'var(--color-danger)';
  const hours = (d.getTime() - Date.now()) / 3600000;
  if (hours < 6) return 'var(--color-warning)';
  return 'inherit';
}

export function ShDashboardPage(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  // Amounts follow the tenant's DISPLAY_NUMBER_FORMAT, not a locale in the source.
  const { money, number } = useBusinessNumber();
  // Instants follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const [tiles, setTiles] = useState<Tiles | null>(null);
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [budget, setBudget] = useState<BudgetSummary | null>(null);
  const [regionSummary, setRegionSummary] = useState<RegionSummaryItem[]>([]);
  const [territorySummary, setTerritorySummary] = useState<TerritorySummaryItem[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    Promise.all([
      apiRequest<Tiles>('/v1/sh/dashboard/tiles', { token }),
      apiRequest<QueueItem[]>('/v1/sh/dashboard/queue', { token }),
      apiRequest<BudgetSummary>('/v1/sh/dashboard/budget-summary', { token }),
      apiRequest<RegionSummaryItem[]>('/v1/sh/dashboard/region-summary', { token }),
      apiRequest<TerritorySummaryItem[]>('/v1/sh/dashboard/territory-summary', { token }),
    ])
      .then(([t, q, b, r, ts]) => {
        if (!alive) return;
        setTiles(t); setQueue(q); setBudget(b);
        setRegionSummary(r); setTerritorySummary(ts);
      })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token]);

  if (error) return <main aria-label="sh-dashboard"><p role="alert" style={{ color: 'var(--color-danger)' }}>{error}</p></main>;
  if (!tiles) return <main aria-label="sh-dashboard"><p>{t('sh.common.loading')}</p></main>;

  return (
    <main aria-label="sh-dashboard">
      <h1 style={{ marginBottom: 16 }}>{t('sh.dash.title')}</h1>

      {/* ── Tile grid ────────────────────────────────── */}
      <section aria-label="sh-tiles" style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 24 }}>
        {([
          ['pending-review', 'sh.dash.tilePendingReview', tiles.pendingReview],
          ['in-review', 'sh.dash.tileInReview', tiles.inReview],
          ['approved', 'sh.dash.tileApproved', tiles.approved],
          ['rejected', 'sh.dash.tileRejected', tiles.rejected],
          ['clarification', 'sh.dash.tileClarification', tiles.clarificationRequired],
          ['escalated', 'sh.dash.tileEscalated', tiles.escalated],
          ['on-hold', 'sh.dash.tileOnHold', tiles.onHold],
          ['finance-eligible', 'sh.dash.tileFinanceEligible', tiles.financeEligible],
          ['overdue', 'sh.dash.tileOverdue', tiles.overdue],
          ['open-exceptions', 'sh.dash.tileOpenExceptions', tiles.openExceptions],
          ['pending-escalations', 'sh.dash.tilePendingEscalations', tiles.pendingEscalations],
          ['pending-clarifications', 'sh.dash.tilePendingClarifications', tiles.pendingClarifications],
        ] as const).map(([slug, labelKey, val]) => (
          <div key={slug} style={TILE_STYLE} data-testid={`tile-${slug}`}>
            <div style={NUM_STYLE}>{val}</div>
            <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{t(labelKey)}</div>
          </div>
        ))}
      </section>

      {/* ── Budget summary ───────────────────────────── */}
      {budget && (
        <section aria-label="sh-budget-summary" style={{
          padding: 16, borderRadius: 8, marginBottom: 24,
          border: '1px solid var(--color-border-default)',
          background: 'var(--color-bg-elevated)',
        }}>
          <h2 style={{ fontSize: 16, marginBottom: 8 }}>{t('sh.dash.budgetOverview')}</h2>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            <div><strong>{t('sh.dash.allocated')}</strong> ₹{money(budget.totalAllocated)}</div>
            <div><strong>{t('sh.dash.consumed')}</strong> ₹{money(budget.totalConsumed)}</div>
            <div><strong>{t('sh.dash.reserved')}</strong> ₹{money(budget.totalReserved)}</div>
            <div><strong>{t('sh.dash.utilization')}</strong> {number(budget.utilizationPct)}%</div>
            <div><strong>{t('sh.dash.activePlans')}</strong> {budget.activePlans}</div>
          </div>
          <div style={{ marginTop: 8 }}>
            <Link to="/sh/budget" data-testid="link-budget-detail">{t('sh.dash.viewBudgetPlans')}</Link>
          </div>
        </section>
      )}

      {/* ── Region-wise Summary ──────────────────────── */}
      {regionSummary.length > 0 && (
        <section aria-label="sh-region-summary" style={{
          padding: 16, borderRadius: 8, marginBottom: 24,
          border: '1px solid var(--color-border-default)',
          background: 'var(--color-bg-elevated)',
        }}>
          <h2 style={{ fontSize: 16, marginBottom: 8 }}>{t('sh.dash.regionSummary')}</h2>
          <DataTable<RegionSummaryItem>
            ariaLabel="sh-region-summary-table"
            testId="sh-region-summary-table"
            rows={regionSummary}
            rowKey={(r) => r.regionCode}
            rowTestId={(r) => `region-${r.regionCode}`}
            columns={[
              { key: 'region', header: t('sh.dash.colRegion'), render: (r) => r.regionCode },
              { key: 'total', header: t('sh.dash.colTotal'), render: (r) => r.total },
              { key: 'approved', header: t('sh.dash.colApproved'), render: (r) => r.approved },
              { key: 'pending', header: t('sh.dash.colPending'), render: (r) => r.pending },
            ]}
          />
        </section>
      )}

      {/* ── Territory-wise Summary ───────────────────── */}
      {territorySummary.length > 0 && (
        <section aria-label="sh-territory-summary" style={{
          padding: 16, borderRadius: 8, marginBottom: 24,
          border: '1px solid var(--color-border-default)',
          background: 'var(--color-bg-elevated)',
        }}>
          <h2 style={{ fontSize: 16, marginBottom: 8 }}>{t('sh.dash.territorySummary')}</h2>
          <DataTable<TerritorySummaryItem>
            ariaLabel="sh-territory-summary-table"
            testId="sh-territory-summary-table"
            rows={territorySummary}
            rowKey={(ts) => ts.territoryCode}
            rowTestId={(ts) => `territory-${ts.territoryCode}`}
            columns={[
              { key: 'territory', header: t('sh.dash.colTerritory'), render: (ts) => ts.territoryCode },
              { key: 'total', header: t('sh.dash.colTotal'), render: (ts) => ts.total },
              { key: 'approved', header: t('sh.dash.colApproved'), render: (ts) => ts.approved },
              { key: 'pending', header: t('sh.dash.colPending'), render: (ts) => ts.pending },
            ]}
          />
        </section>
      )}

      {/* ── Review queue ─────────────────────────────── */}
      <section aria-label="sh-queue">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <h2 style={{ fontSize: 16 }}>{t('sh.dash.reviewQueue')}</h2>
          <div style={{ display: 'flex', gap: 12 }}>
            <Link to="/sh/queue" data-testid="link-approval-queue">{t('sh.dash.fullQueue')}</Link>
            <Link to="/sh/exceptions" data-testid="link-exceptions">{t('sh.dash.exceptionsLink')}</Link>
          </div>
        </div>
        {queue.length === 0 ? (
          <p style={{ color: 'var(--color-text-muted)' }}>{t('sh.dash.emptyQueue')}</p>
        ) : (
          <DataTable<QueueItem>
            ariaLabel="sh-queue-table"
            rows={queue}
            rowKey={(r) => r.reviewId}
            rowAriaLabel={(r) => `row-${r.reviewId}`}
            columns={[
              { key: 'job', header: t('sh.common.job'), render: (r) => r.jobNumber ?? '—' },
              { key: 'status', header: t('sh.common.status'), render: (r) => <span aria-label={`status-${r.reviewId}`}>{r.status}</span> },
              { key: 'priority', header: t('sh.common.priority'), render: (r) => r.priorityCode ?? '—' },
              { key: 'slaDue', header: t('sh.common.slaDue'), render: (r) => <span style={{ color: slaColor(r.slaDeadlineAt) }}>{at(r.slaDeadlineAt)}</span> },
              { key: 'cost', header: t('sh.common.cost'), render: (r) => money(r.estimatedCostInr) },
              { key: 'hv', header: t('sh.common.hv'), render: (r) => (r.isHighValue ? '🔺' : '—') },
              { key: 'cycle', header: t('sh.common.cycle'), render: (r) => String(r.cycleNo) },
              {
                key: 'actions', header: '',
                render: (r) => (
                  <Link to={`/sh/review/${r.reviewId}`} aria-label={`open-${r.reviewId}`} data-testid={`open-review-${r.reviewId}`}>
                    {t('sh.common.open')}
                  </Link>
                ),
              },
            ]}
          />
        )}
      </section>
    </main>
  );
}
